package com.havn.app.features.auth.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.features.auth.data.isHavnAuthCancellation
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class AuthViewModel(
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(AuthUiState())
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()
    private var ensuredProfileUserId: String? = null

    init {
        scope.launch {
            authRepository.authState.collect { authState ->
                when (authState) {
                    is AuthState.LoggedIn -> {
                        _uiState.value = _uiState.value.copy(authState = authState, isSubmitting = false)
                        if (ensuredProfileUserId != authState.user.id) {
                            ensuredProfileUserId = authState.user.id
                            ensureAgentProfile(authState)
                        }
                    }
                    AuthState.LoggedOut -> {
                        ensuredProfileUserId = null
                        _uiState.value = _uiState.value.signedOut(authState)
                    }
                    else -> {
                        _uiState.value = _uiState.value.copy(authState = authState)
                    }
                }
            }
        }
        scope.launch {
            authRepository.awaitSessionRestore()
            _uiState.value = _uiState.value.copy(isSessionRestoreComplete = true)
        }
    }

    fun showWelcome() {
        _uiState.value = _uiState.value.copy(step = AuthStep.Welcome, submitError = null, statusMessage = null)
    }

    fun showEmailEntry() {
        _uiState.value = _uiState.value.copy(step = AuthStep.EmailEntry, submitError = null, statusMessage = null)
    }

    fun onEmailChanged(email: String) {
        _uiState.value = _uiState.value.copy(email = email, emailError = null, submitError = null, statusMessage = null)
    }

    fun onOtpCodeChanged(code: String) {
        val digits = code.filter { it.isDigit() }.take(MaxOtpCodeLength)
        _uiState.value = _uiState.value.copy(otpCode = digits, otpCodeError = null, submitError = null)
    }

    fun sendEmailOtp() {
        val state = _uiState.value
        val emailError = validateEmail(state.email)
        if (emailError != null) {
            _uiState.value = state.copy(emailError = emailError, submitError = null, statusMessage = null)
            return
        }

        _uiState.value = state.copy(isSubmitting = true, submitError = null, statusMessage = null)
        scope.launch {
            if (authRepository.isAppReviewEmail(state.email)) {
                _uiState.value = _uiState.value.copy(
                    isSubmitting = false,
                    step = AuthStep.VerifyCode,
                    submitError = null,
                    statusMessage = "Enter the app review code to continue."
                )
                return@launch
            }
            val result = authRepository.sendEmailOtp(state.email)
            _uiState.value = _uiState.value.copy(
                isSubmitting = false,
                step = if (result.isSuccess) AuthStep.VerifyCode else _uiState.value.step,
                submitError = result.exceptionOrNull()?.userFacingMessage(),
                statusMessage = if (result.isSuccess) "We sent a sign-in code and magic link to ${state.email.trim()}." else null
            )
            result.exceptionOrNull()?.let {
                HavnLogger.error("AuthViewModel", "Email OTP request failed", it)
            }
        }
    }

    fun verifyEmailOtp() {
        val state = _uiState.value
        val emailError = validateEmail(state.email)
        val codeError = validateOtpCode(state.otpCode)
        if (emailError != null || codeError != null) {
            _uiState.value = state.copy(
                emailError = emailError,
                otpCodeError = codeError,
                submitError = null
            )
            return
        }

        _uiState.value = state.copy(isSubmitting = true, submitError = null)
        scope.launch {
            val result = if (authRepository.isAppReviewEmail(state.email)) {
                authRepository.signInWithAppReviewCode(state.email, state.otpCode)
            } else {
                authRepository.verifyEmailOtp(state.email, state.otpCode)
            }
            _uiState.value = _uiState.value.copy(
                isSubmitting = false,
                submitError = result.exceptionOrNull()?.userFacingMessage()
            )
            result.exceptionOrNull()?.let {
                HavnLogger.error("AuthViewModel", "Email OTP verification failed", it)
            }
        }
    }

    fun signInWithGoogle() {
        _uiState.value = _uiState.value.copy(isSubmitting = true, submitError = null, statusMessage = null)
        scope.launch {
            val result = authRepository.signInWithGoogle()
            _uiState.value = _uiState.value.copy(
                isSubmitting = false,
                submitError = result.exceptionOrNull()?.userFacingMessage()
            )
            result.exceptionOrNull()?.let {
                HavnLogger.error("AuthViewModel", "Google sign-in failed", it)
            }
        }
    }

    fun signInWithApple() {
        _uiState.value = _uiState.value.copy(isSubmitting = true, submitError = null, statusMessage = null)
        scope.launch {
            val result = authRepository.signInWithApple()
            val failure = result.exceptionOrNull()
            if (failure?.isHavnAuthCancellation() == true) {
                _uiState.value = _uiState.value.copy(isSubmitting = false, submitError = null, statusMessage = null)
                return@launch
            }
            _uiState.value = _uiState.value.copy(
                isSubmitting = false,
                submitError = failure?.userFacingMessage()
            )
            failure?.let {
                HavnLogger.error("AuthViewModel", "Apple sign-in failed", it)
            }
        }
    }

    fun logout() {
        ensuredProfileUserId = null
        _uiState.value = _uiState.value.copy(isSubmitting = true, submitError = null)
        scope.launch {
            val result = authRepository.logout()
            _uiState.value = _uiState.value.copy(
                isSubmitting = false,
                step = if (result.isSuccess) AuthStep.Welcome else _uiState.value.step,
                otpCode = if (result.isSuccess) "" else _uiState.value.otpCode,
                otpCodeError = null,
                statusMessage = null,
                submitError = result.exceptionOrNull()?.userFacingMessage()
            )
            result.exceptionOrNull()?.let {
                HavnLogger.error("AuthViewModel", "Logout submit failed", it)
            }
        }
    }

    fun clearSubmitError() {
        _uiState.value = _uiState.value.copy(submitError = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private fun ensureAgentProfile(authState: AuthState.LoggedIn) {
        scope.launch {
            authRepository.ensureAgentProfile(authState.user).exceptionOrNull()?.let {
                HavnLogger.error("AuthViewModel", "Agent profile setup failed", it)
            }
        }
    }

    private fun validateEmail(value: String): String? {
        val email = value.trim()
        return when {
            email.isBlank() -> "Email is required."
            "@" !in email || "." !in email.substringAfter("@", "") -> "Enter a valid email address."
            else -> null
        }
    }

    private fun validateOtpCode(value: String): String? =
        when {
            value.isBlank() -> "Enter the code from your email."
            value.length !in 6..MaxOtpCodeLength -> "Enter the full code from your email."
            else -> null
        }

    private companion object {
        const val MaxOtpCodeLength = 8
    }
}

enum class AuthStep {
    Welcome,
    EmailEntry,
    VerifyCode
}

data class AuthUiState(
    val authState: AuthState = AuthState.Loading,
    val isSessionRestoreComplete: Boolean = false,
    val step: AuthStep = AuthStep.Welcome,
    val email: String = "",
    val otpCode: String = "",
    val emailError: String? = null,
    val otpCodeError: String? = null,
    val submitError: String? = null,
    val statusMessage: String? = null,
    val isSubmitting: Boolean = false
)

private fun AuthUiState.signedOut(authState: AuthState = AuthState.LoggedOut): AuthUiState =
    copy(
        authState = authState,
        isSubmitting = false,
        step = AuthStep.Welcome,
        otpCode = "",
        emailError = null,
        otpCodeError = null,
        submitError = null,
        statusMessage = null
    )

private fun Throwable.userFacingMessage(): String {
    val rawMessage = message?.takeIf { it.isNotBlank() }
        ?: return "Something went wrong. Please try again."
    val normalized = rawMessage.lowercase()
    return when {
        "unsupported provider" in normalized || "provider is not enabled" in normalized ->
            "This sign-in option is not enabled yet. Please use email for now."
        "otp_expired" in normalized || "token has expired" in normalized ->
            "This code has expired. Please request a new one."
        "invalid token" in normalized || "invalid otp" in normalized ->
            "That code does not look right. Please check the email and try again."
        "user not found" in normalized || "signup" in normalized || "signups" in normalized ->
            "Could not create or sign in to this account. Please try again."
        else -> rawMessage.havnFriendlyMessage("Could not sign in. Please try again.")
    }
}
